/**
 * Las cuentas y los textos de una cotizacion (COT-01).
 *
 * `totalesCotizacion` es copia de `backend/src/utilidades/cotizacion.ts`: el
 * formulario muestra los totales mientras se arma, y `compartido/` lleva solo
 * tipos. La que vale es la del servidor, que es quien guarda la cotizacion.
 */
import type { ImagenProducto, ProductoAdminResumen, TotalesCotizacion } from '@bandu/compartido';

import { nombreConVariante } from './producto';

const TASA_IVA = 0.19;

export const VALIDEZ_POR_DEFECTO = 30;

/** En la lista, `?cotizacion=<id>` abre esa cotizacion en el modal. */
export const PARAMETRO_COTIZACION = 'cotizacion';

/** Precio sin IVA, redondeado al peso. */
export function netoDe(precioConIva: number): number {
  return Math.round(precioConIva / (1 + TASA_IVA));
}

/** El descuento se aplica al neto y al total por separado, con el mismo porcentaje. */
export function totalesCotizacion(
  lineas: ReadonlyArray<{ precioUnitario: number; cantidad: number }>,
  descuentoPorcentaje: number,
): TotalesCotizacion {
  const subtotalNeto = lineas.reduce((suma, linea) => suma + netoDe(linea.precioUnitario) * linea.cantidad, 0);
  const subtotal = lineas.reduce((suma, linea) => suma + linea.precioUnitario * linea.cantidad, 0);

  const descuentoNeto = Math.round((subtotalNeto * descuentoPorcentaje) / 100);
  const totalNeto = subtotalNeto - descuentoNeto;
  const total = subtotal - Math.round((subtotal * descuentoPorcentaje) / 100);

  return { subtotalNeto, descuentoNeto, totalNeto, iva: total - totalNeto, total };
}

/**
 * Las observaciones con que nace una cotizacion: las que el negocio ya ponia en
 * las que armaba a mano. Se pueden cambiar en cada una.
 */
export function observacionesPorDefecto(validezDias: number): string {
  return (
    'Precios expresados en pesos chilenos. Valores netos calculados sobre precios IVA incluido ' +
    `considerando IVA de 19%. Cotización válida por ${validezDias} días y sujeta a disponibilidad de stock.`
  );
}

/** `Cotizacion_Bandu_COT-2026-0001_Bomberos_de_Llanquihue.pdf`: sin acentos ni espacios. */
export function nombreArchivoCotizacion(cotizacion: {
  numero: string;
  cliente: { nombre: string };
}): string {
  const cliente = cotizacion.cliente.nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
  return `Cotizacion_Bandu_${cotizacion.numero}${cliente.length > 0 ? `_${cliente}` : ''}.pdf`;
}

/** Un producto de la cotizacion mientras se arma. */
export interface LineaCotizacion {
  productoId: number;
  sku: string;
  nombre: string;
  /** Con IVA incluido, el de la tienda hoy. */
  precioUnitario: number;
  stock: number;
  imagen: ImagenProducto | null;
  /** Lo escrito en el campo; vacio mientras se corrige. */
  cantidad: string;
}

export function lineaDesdeProducto(
  producto: Pick<ProductoAdminResumen, 'id' | 'sku' | 'nombre' | 'etiquetaVariante' | 'precio' | 'stock' | 'imagen'>,
  cantidad = 1,
): LineaCotizacion {
  return {
    productoId: producto.id,
    sku: producto.sku,
    nombre: nombreConVariante(producto),
    precioUnitario: producto.precio,
    stock: producto.stock,
    imagen: producto.imagen,
    cantidad: String(cantidad),
  };
}

/** Cantidad valida o cero: una linea a medio escribir no suma. */
export function cantidadDe(linea: LineaCotizacion): number {
  const valor = Number(linea.cantidad);
  return Number.isInteger(valor) && valor > 0 ? valor : 0;
}
